import { faCaretDown, faCaretUp, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  BoldBodyText,
  Button,
  Card,
  Currency,
  FetchStatusFlow,
  Grid,
  Header1Text,
  Header3Text,
  SpaceWrapper,
  TinyBodyText,
} from 'ui-toolkit';
import { IPageData } from 'types/rest/IPageData';
import { IFetchDataState } from '../../store/IFetchDataState';
import { calculateRemainingSplitValue } from '../../utilities/accounting/calculateRemainingSplitValue';
import { ICreateQueryFromAmountCents } from '../../utilities/accounting/createQueryFromAmountCents';
import { ExpectedDetailCard } from './ExpectedDetailCard';
import { SplitTransactionCard } from './SplitTransactionCard';
import { MatchingContainer } from './matchingContainers/MatchingContainer';
import { eIncomingFundsType } from './matchingContainers/eIncomingFundsType';
import { IEscrowExpectedIncomingData } from 'types/accounting/data/IEscrowExpectedIncomingData';
import { IEscrowExpectedIncomingQuery } from 'types/accounting/data/IEscrowExpectedIncomingQuery';
import { IEscrowActualIncomingData } from 'types/accounting/data/IEscrowActualIncomingData';
import { useHasFeatureFlag } from 'selectors/useHasFeatureFlag';
import { useLocation } from 'react-router-dom';
import { ClientUrlFactory } from 'ui-toolkit/rest/ClientUrlFactory';

const Header1PrimaryColorText = styled(Header1Text)`
  color: ${(props) => props.theme.colors.primary};
`;
const StyledSplitButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.spacing4};
  justify-content: center;
`;

// 516px is used in here as a "Magic Number".
// It's about 2 "Matched" panels + part of another one.
// While the users screen might be larger (or smaller) this was a small enough
// amount that it looked like there was something below to scroll into view
// In the future a better UX will be developed for this for us.
// This is the first time the UX team has had to deal with this type of thing.
const ScrollingWrapper = styled.div`
  max-height: 516px;
  margin-bottom: ${(props) => props.theme.spacing.spacing4};
  overflow: scroll;
`;

export interface IActualIncomingDetailProps<Data, ExpectedQuery, ExpectedData> {
  createQueryFromAmountCents: ICreateQueryFromAmountCents<ExpectedQuery>;
  getExpectedDataFromData: (data: Data) => ExpectedData[];
  hasEditPermission: boolean;
  hasExpectedReadPermission: boolean;
  intro: string;
  type: eIncomingFundsType;
  onMatch: (match: ExpectedData) => void;
  onUnmatch: (match: ExpectedData) => void;
  selectedIncomingActual: Data;
  setSuggestedExpectedQuery: (x: ExpectedQuery) => void;
  showDetailsOfSelected: (data: Data) => ReactNode;
  suggestedExpectedResults: IFetchDataState<ExpectedQuery, IPageData<ExpectedData>>;
}

export const ActualIncomingDetailContainer = <
  Data extends IEscrowActualIncomingData,
  ExpectedQuery extends IEscrowExpectedIncomingQuery,
  ExpectedData extends IEscrowExpectedIncomingData,
>({
  createQueryFromAmountCents,
  getExpectedDataFromData,
  hasEditPermission,
  hasExpectedReadPermission,
  intro,
  type,
  onMatch,
  onUnmatch,
  selectedIncomingActual,
  setSuggestedExpectedQuery,
  showDetailsOfSelected,
  suggestedExpectedResults,
}: IActualIncomingDetailProps<Data, ExpectedQuery, ExpectedData>) => {
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [showSplit, setShowSplit] = useState<boolean>(false);
  const [showMatchSearch, setShowMatchSearch] = useState(false);
  const { fetchStatus, fetchError, data } = suggestedExpectedResults;

  const { amountCents } = selectedIncomingActual;
  const expected = getExpectedDataFromData(selectedIncomingActual);
  const amountCentsMax = calculateRemainingSplitValue(amountCents, expected);
  const isAchPage = useLocation().pathname === ClientUrlFactory().urlFor.incomingAchs();
  const isAchMatchingEnabled = useHasFeatureFlag('fees.ach-matching.automated-unrestricted-internal-transfer');
  const isSplittable = (!isAchPage || isAchMatchingEnabled) && amountCentsMax > 0;
  const hasMatches = !!expected.length;

  useEffect(() => {
    setShowDetails(false);
    setShowSplit(false);
  }, [selectedIncomingActual, setShowDetails, setShowSplit]);

  const getSuggestion = useCallback(
    (amountCents: number) => {
      if (!hasMatches) {
        setSuggestedExpectedQuery(createQueryFromAmountCents(amountCents, amountCents, 1));
      }
    },
    [hasMatches, setSuggestedExpectedQuery, createQueryFromAmountCents]
  );

  useEffect(() => {
    getSuggestion(selectedIncomingActual.amountCents);
  }, [selectedIncomingActual, getSuggestion]);

  const [searchAmountCents, setSearchAmountCentsBase] = useState<number>(0);
  const setSearchAmountCents = useCallback(
    (amount: number): void => {
      setSearchAmountCentsBase(amount);
      setShowMatchSearch(true);
    },
    [setSearchAmountCentsBase, setShowMatchSearch]
  );

  return (
    <Card title={<Header3Text>Transaction Details</Header3Text>}>
      <Button
        intent={'ghost'}
        isInline
        type="button"
        onClick={() => setShowDetails(!showDetails)}
        data-test-id={'Additional-Info'}
      >
        Additional Info <FontAwesomeIcon icon={showDetails ? faCaretUp : faCaretDown} />
      </Button>
      {showDetails && showDetailsOfSelected(selectedIncomingActual)}
      <BoldBodyText>Received Amount</BoldBodyText>
      <Header1PrimaryColorText>
        <Currency value={amountCents} valueIsCents={true} />
      </Header1PrimaryColorText>
      <ScrollingWrapper>
        {expected.map((match) => (
          <ExpectedDetailCard
            key={match.id}
            item={match}
            selectedIncomingActual={selectedIncomingActual}
            header={'Matched to Deposit'}
            footer={
              <SpaceWrapper leftSpace={'none'} rightSpace={'none'} topSpace={'default'} bottomSpace={'none'}>
                <Button disabled={!hasEditPermission} intent={'danger'} type="button" onClick={() => onUnmatch(match)}>
                  Unmatch
                </Button>
              </SpaceWrapper>
            }
          />
        ))}
        {!hasMatches && !showSplit && (
          <FetchStatusFlow fetchStatus={fetchStatus} error={fetchError}>
            {data && data.content.length !== 1 && (
              <SpaceWrapper leftSpace={'none'} rightSpace={'none'}>
                <Card>
                  <TinyBodyText>No Suggested Match</TinyBodyText>
                </Card>
              </SpaceWrapper>
            )}
            {data && data.content.length === 1 && (
              <ExpectedDetailCard
                item={data.content[0]}
                selectedIncomingActual={selectedIncomingActual}
                header={'Suggested Match'}
                footer={
                  <SpaceWrapper leftSpace={'none'} rightSpace={'none'} topSpace={'default'} bottomSpace={'none'}>
                    <Button
                      intent={'secondary'}
                      type="button"
                      onClick={() => onMatch(data.content[0])}
                      disabled={!hasEditPermission}
                    >
                      Match
                    </Button>
                  </SpaceWrapper>
                }
              />
            )}
          </FetchStatusFlow>
        )}
        {showSplit && (
          <SplitTransactionCard
            amountCents={amountCentsMax}
            findMatch={(amount) => setSearchAmountCents(amount)}
            cancelSplit={() => setShowSplit(false)}
          />
        )}
      </ScrollingWrapper>
      <Grid columnCount={2} columnWidth={'max-content'}>
        {!showSplit && !hasMatches && (
          <Button
            intent={'primary'}
            type="button"
            onClick={() => setSearchAmountCents(amountCents)}
            disabled={!hasExpectedReadPermission}
          >
            Find Match
          </Button>
        )}
        {!showSplit && isSplittable && (
          <StyledSplitButton
            intent={'secondary'}
            type="button"
            onClick={() => setShowSplit(true)}
            disabled={!hasEditPermission}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Create Split</span>
          </StyledSplitButton>
        )}
      </Grid>
      {showMatchSearch && hasExpectedReadPermission && (
        <MatchingContainer
          intro={intro}
          onClose={() => setShowMatchSearch(false)}
          amountCents={searchAmountCents}
          amountCentsMax={amountCentsMax}
          type={type}
          actualId={selectedIncomingActual.id}
          accountNumber={selectedIncomingActual.accountNumber || ''}
          hasEditPermission={hasEditPermission}
        />
      )}
    </Card>
  );
};
